import classNames from 'classnames'
import React, { forwardRef, useCallback, useState } from 'react'
import { CloseButton } from '../../common/components/button/CloseButton'
import { LANGUAGE_LABELS, USER_MENU_LANGUAGE, USER_MENU_PLAYER } from '../../common/constants'
import shared from '../../common/styles/shared.module.css'
import { useUser } from '../../contexts/userContext'
import { Language } from '../../types/commonTypes'
import styles from './UserMenu.module.css'
import { useGameData } from '../../contexts/PlayerAccountGamesContext'
import { PlayerAccountMenu } from './components/PlayerAccountMenu/PlayerAccountMenu'
import { LanguageMenu } from './components/LanguageMenu/LanguageMenu'
import { useNotification } from '../../contexts/NotificationContext'
import { useTranslation } from 'react-i18next'
import { ThreeDots } from '../../common/components/loaders/ThreeDots/ThreeDots'
import { getClientInfo } from '../../util/browserSpecifics'

type PlayerUserMenuProps = {
  containerStyle?: React.CSSProperties
  isUserMenuOpen: boolean
  onMenuClose: () => void
  onMenuItemClick?: () => void
}

export const PlayerUserMenuContainer = forwardRef<HTMLDivElement, PlayerUserMenuProps>(
  ({ onMenuClose, containerStyle, isUserMenuOpen }, ref) => {
    const { user, doLogout } = useUser()
    const { updatePlayerAccount, getPlayerAccountPasswordToken } = useGameData()
    const { notifySuccess, notifyError } = useNotification()
    const { t } = useTranslation()
    const [isLoading, setIsLoading] = useState(false)
    const onContainerClick = (e: React.SyntheticEvent<HTMLDivElement>) => {
      e.stopPropagation()
    }

    const [menuType, setMenuType] = React.useState(USER_MENU_PLAYER)

    React.useEffect(() => {
      return () => {
        setMenuType(USER_MENU_PLAYER)
      }
    }, [])

    const handleBackItemClick = (menuType: string) => {
      setMenuType(USER_MENU_PLAYER)
    }

    const onCloseButtonClick = React.useCallback(
      (e: React.SyntheticEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        onMenuClose()
        setMenuType(USER_MENU_PLAYER)
      },
      [onMenuClose],
    )

    const handleInitialMenuItemClick = (menuItem: string) => {
      if (menuItem === 'logout') {
        doLogout()
      } else if (menuItem === 'help') {
        handleContactUs()
      } else if (menuItem === 'reset-password') {
        getPasswordResetUrl()
      } else if (menuItem === USER_MENU_LANGUAGE) {
        setMenuType(USER_MENU_LANGUAGE)
      }
    }

    const getPasswordResetUrl = async () => {
      setIsLoading(true)
      const result = await getPlayerAccountPasswordToken(user?.email || '')
      if (result) {
        notifySuccess({
          title: t(
            'settings_general.profile.reset_password_notification.success.title',
            'Password reset link sent to your email',
          ),
          content: t(
            'settings_general.profile.reset_password_notification.success.content',
            'A password reset link was sent to your email address.',
          ),
        })
      } else {
        notifyError({
          title: t('settings_general.profile.reset_password_notification.failure.title', 'Password reset failed'),
          content: t(
            'settings_general.profile.reset_password_notification.failure.content',
            'An error occurred while requesting a password reset. Please try again or contact us for support.',
          ),
        })
      }
      setIsLoading(false)
      setMenuType(USER_MENU_PLAYER)
      onMenuClose()
    }

    const handleContactUs = useCallback(() => {
      if (!window.Beacon) {
        console.warn('HelpScout Beacon is not available')
        return
      }

      const clientInfo = getClientInfo()

      const identifyData: Record<string, any> = {}

      if (user?.contactName) identifyData.name = user.contactName
      if (user?.email) identifyData.email = user.email
      if (user?.activeBusiness?.id) identifyData['business-id'] = user.activeBusiness.id
      if (user?.activeBusiness?.industry) identifyData.industry = user.activeBusiness.industry
      if (user?.activeBusiness?.name) identifyData.company = user.activeBusiness.name

      // Always include client info if available
      identifyData['browser-name'] = clientInfo.browser
      identifyData['device-type'] = clientInfo.deviceType
      identifyData.platform = clientInfo.platform
      identifyData['user-agent'] = clientInfo.userAgent

      window.Beacon('identify', identifyData)

      window.Beacon('open')
    }, [
      user?.contactName,
      user?.email,
      user?.activeBusiness.id,
      user?.activeBusiness.industry,
      user?.activeBusiness.name,
    ])

    const handleLanguageMenuItemClick = (name: string, value: string) => {
      updatePlayerAccount(undefined, value)
      setMenuType(USER_MENU_PLAYER)
    }

    if (isUserMenuOpen == null || isUserMenuOpen === false) {
      return null
    }

    return (
      <>
        {isLoading && (
          <>
            <div className={styles.loaderBackgroundOverlay} />
            <div className={styles.loaderContainer}>
              <ThreeDots />
            </div>
          </>
        )}
        <div
          ref={ref}
          onClick={onContainerClick}
          className={classNames(styles.container, shared.smallShadow, shared.col)}
          style={containerStyle}
        >
          <div className={classNames(styles.header)}>
            <p className={classNames(styles.profileInitials)}>{user?.userInitials}</p>
            <span className={styles.namesContainer}>
              <span className={classNames(styles.profileName)}>{user?.name || user?.chatName}</span>
              <span className={classNames(styles.profileEmail)}>{user?.email}</span>
            </span>

            <CloseButton autoFocus onClick={onCloseButtonClick} style={{ fontSize: '1rem' }} />
          </div>
          <div className={styles.rowSeparator} />
          {menuType === USER_MENU_LANGUAGE && (
            <LanguageMenu onMenuItemClick={handleLanguageMenuItemClick} onMenuBackClick={handleBackItemClick} />
          )}
          {menuType === USER_MENU_PLAYER && (
            <PlayerAccountMenu
              onMenuItemClick={handleInitialMenuItemClick}
              activeLanguage={LANGUAGE_LABELS[(user?.language as Language) ?? Language.ENGLISH]}
            />
          )}
        </div>
      </>
    )
  },
)
